import React, { useState, useEffect } from 'react'
import { 
  Card, 
  Form, 
  Input, 
  Select, 
  DatePicker, 
  Button, 
  Space,
  Typography,
  message,
  Spin,
  Modal
} from 'antd'
import { 
  ArrowLeftOutlined,
  CalendarOutlined,
  ClockCircleOutlined,
  UserOutlined,
  PhoneOutlined,
  ShopOutlined
} from '@ant-design/icons'
import { useNavigate, useLocation } from 'react-router-dom'
import { useAuth } from '../hooks/useAuth'
import { useLockBodyScrollWhenDropdownOpen } from '../hooks/useLockBodyScrollWhenDropdownOpen'
import { carSeriesAPI, dealerAPI, invitationAPI } from '../services/api'
import './TestDriveInvitationPage.css'

const { Title } = Typography
const { Option } = Select

interface Dealer {
  id: number
  name: string
  dealer_name: string
  address: string
  wecom_dept_id?: string
  sj_code?: string
  coordinates?: string
  business_status?: string
  phone?: string
  contact_person?: string
}

interface CarSeries {
  id: number
  series_name: string
  template_images?: any
  template_colors?: any
  is_enabled: boolean
  created_at?: string
  updated_at?: string
}

const TestDriveInvitationPage: React.FC = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const { user, loading: authLoading, updateUser } = useAuth()
  const [form] = Form.useForm()
  const [selectedDealer, setSelectedDealer] = useState<Dealer | null>(null)
  const [carSeries, setCarSeries] = useState<CarSeries[]>([])
  const [loading, setLoading] = useState(true)
  const [successModalVisible, setSuccessModalVisible] = useState(false)
  const [createdInvitationId, setCreatedInvitationId] = useState<string | null>(null)
  const [sendingMessage, setSendingMessage] = useState(false)

  // 从路由状态中获取选中的经销商
  useEffect(() => {
    if (location.state?.selectedDealer) {
      setSelectedDealer(location.state.selectedDealer)
    }
  }, [location.state])

  // 加载车系数据和用户经销商信息
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true)
        
        // 并行加载车系和用户经销商信息
        const [carSeriesResponse, userDealerResponse] = await Promise.all([
          carSeriesAPI.getAllCarSeries(),
          dealerAPI.getCurrentConsultantDealer()
        ])

        // 处理车系数据
        if ((carSeriesResponse as any).success) {
          setCarSeries((carSeriesResponse as any).data.car_series || [])
        } else {
          message.error('获取车系数据失败')
        }

        // 处理用户经销商数据
        if ((userDealerResponse as any).success && (userDealerResponse as any).data) {
          console.log('用户经销商数据:', (userDealerResponse as any).data)
          const dealerData = (userDealerResponse as any).data.dealer
          // 如果没有从路由状态获取到经销商，使用用户默认经销商
          if (!location.state?.selectedDealer && dealerData) {
            setSelectedDealer(dealerData)
          }
        } else {
          console.log('用户经销商响应:', userDealerResponse)
        }

      } catch (error) {
        console.error('加载数据失败:', error)
        message.error('加载数据失败')
      } finally {
        setLoading(false)
      }
    }

    loadData()
  }, [location.state])

  // 监听用户认证状态和用户数据变化，设置电话号码默认值
  useEffect(() => {
    // 只有在认证加载完成且用户数据存在时才设置默认值
    if (!authLoading && user?.consultant_phone) {
      console.log('设置用户电话号码默认值:', user.consultant_phone)
      form.setFieldsValue({
        consultantPhone: user.consultant_phone
      })
    }
  }, [authLoading, user?.consultant_phone, form])

  // 生成小时选项 (0-23)
  const hourOptions = Array.from({ length: 24 }, (_, i) => ({
    value: i,
    label: `${i.toString().padStart(2, '0')}时`
  }))

  // 生成分钟选项 (0, 10, 20, 30, 40, 50)
  const minuteOptions = Array.from({ length: 6 }, (_, i) => ({
    value: i * 10,
    label: `${(i * 10).toString().padStart(2, '0')}分`
  }))

  // 禁用过去日期
  const disabledDate = (current: any) => {
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    return current && current < today
  }

  // 根据选择的日期生成可用的小时选项
  const getAvailableHourOptions = () => {
    const selectedDate = form.getFieldValue('appointmentDate')
    if (!selectedDate) {
      return hourOptions
    }

    const today = new Date()
    const selectedDateStr = selectedDate.format('YYYY-MM-DD')
    const todayStr = today.toISOString().split('T')[0]

    if (selectedDateStr === todayStr) {
      // 如果是今天，只显示当前时间之后的小时
      const currentHour = today.getHours()
      return hourOptions.filter(hour => hour.value > currentHour)
    }

    return hourOptions
  }

  // 根据选择的日期和小时生成可用的分钟选项
  const getAvailableMinuteOptions = () => {
    const selectedDate = form.getFieldValue('appointmentDate')
    const selectedHour = form.getFieldValue('appointmentHour')
    
    if (!selectedDate || selectedHour === undefined) {
      return minuteOptions
    }

    const today = new Date()
    const selectedDateStr = selectedDate.format('YYYY-MM-DD')
    const todayStr = today.toISOString().split('T')[0]

    if (selectedDateStr === todayStr && selectedHour === today.getHours()) {
      // 如果是今天且是当前小时，只显示当前分钟之后的分钟
      const currentMinute = today.getMinutes()
      return minuteOptions.filter(minute => minute.value > currentMinute)
    }

    return minuteOptions
  }

  const handleBack = () => {
    navigate('/dashboard')
  }

  const handleDealerSelect = () => {
    navigate('/dealer-selection')
  }

  const handleCancel = () => {
    navigate('/dashboard')
  }

  const handleCreate = async () => {
    try {
      // 验证表单
      const values = await form.validateFields()
      
      // 检查是否选择了经销商
      if (!selectedDealer) {
        message.error('请先选择经销商')
        return
      }
      
      // 准备创建邀请函的参数
      const createParams = {
        customerName: values.customerName,
        dealerId: selectedDealer.id,
        seriesId: values.carSeries,
        appointmentDate: values.appointmentDate.format('YYYY-MM-DD'),
        appointmentHour: values.appointmentHour,
        appointmentMinute: values.appointmentMinute,
        consultantPhone: values.consultantPhone
      }
      
      console.log('创建邀请函参数:', createParams)
      
      // 显示加载状态
      const loadingMessage = message.loading('正在创建邀请函...', 0)
      
      try {
        // 调用API创建邀请函
        const response = await invitationAPI.createInvitation(createParams) as any
        
        if (response.success) {
          loadingMessage()
          
          // 如果电话号码有变化，更新本地用户信息
          if (user && values.consultantPhone !== user.consultant_phone) {
            const updatedUser = { ...user, consultant_phone: values.consultantPhone }
            updateUser(updatedUser)
            console.log('电话号码已更新:', values.consultantPhone)
          }
          
          // 保存创建的邀请函ID并显示成功弹窗
          setCreatedInvitationId(response.data.invitation.id)
          setSuccessModalVisible(true)
        } else {
          loadingMessage()
          message.error(response.message || '创建邀请函失败')
        }
      } catch (apiError: any) {
        loadingMessage()
        console.error('创建邀请函API错误:', apiError)
        message.error('创建邀请函失败，请重试')
      }
      
    } catch (error: any) {
      console.error('表单验证失败:', error)
      if (error.errorFields) {
        message.error('请填写完整的表单信息')
      }
    }
  }

  const handleSuccessModalOk = async () => {
    if (!createdInvitationId) {
      setSuccessModalVisible(false)
      navigate('/dashboard')
      return
    }

    try {
      setSendingMessage(true)
      
      // 获取邀请函链接
      const response = await invitationAPI.getInvitationLink(createdInvitationId) as any
      
      if (response.success && response.data?.link) {
        // 在企业微信环境中，使用当前窗口打开链接
        window.location.href = response.data.link
      } else {
        message.error(response.message || '获取邀请函链接失败')
      }
    } catch (error) {
      console.error('获取邀请函链接失败:', error)
      message.error('获取邀请函链接失败，请稍后重试')
    } finally {
      setSendingMessage(false)
      setSuccessModalVisible(false)
      navigate('/dashboard')
    }
  }

  const { lock: lockBodyScroll, unlock: unlockBodyScroll } = useLockBodyScrollWhenDropdownOpen()

  return (
    <div className="test-drive-page">
      {/* 顶部导航 */}
      <div className="page-header">
        <div className="header-content">
          <Button
            type="text"
            icon={<ArrowLeftOutlined />}
            onClick={handleBack}
            className="back-btn"
          >
            返回
          </Button>
          <Title level={4} className="page-title">
            创建试乘试驾邀请函
          </Title>
          <div className="header-spacer"></div>
        </div>
      </div>

      {/* 页面内容 */}
      <div className="page-content">
        {loading || authLoading ? (
          <div style={{ textAlign: 'center', padding: '50px' }}>
            <Spin size="large" />
            <div style={{ marginTop: '16px', color: '#666' }}>加载中...</div>
          </div>
        ) : (
          <Form
            form={form}
            layout="vertical"
            className="invitation-form"
          >
          {/* 车系选择 */}
          <Card className="form-card">
            <Form.Item
              label={
                <span className="form-label">
                  <ShopOutlined className="label-icon" />
                  选择车系
                </span>
              }
              name="carSeries"
              rules={[{ required: true, message: '请选择车系' }]}
            >
              <Select
                placeholder="请选择车系"
                size="large"
                className="form-select mobile-select"
                loading={loading}
                dropdownStyle={{
                  fontSize: '16px',
                  minHeight: '48px'
                }}
                onDropdownVisibleChange={(open) => (open ? lockBodyScroll() : unlockBodyScroll())}
                optionFilterProp="children"
                showSearch
                filterOption={(input, option) =>
                  String(option?.children || '').toLowerCase().includes(input.toLowerCase())
                }
              >
                {carSeries.map(series => (
                  <Option key={series.id} value={series.id}>
                    {series.series_name}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Card>

          {/* 客户信息 */}
          <Card className="form-card">
            <Form.Item
              label={
                <span className="form-label">
                  <UserOutlined className="label-icon" />
                  客户姓名
                </span>
              }
              name="customerName"
              rules={[{ required: true, message: '请输入客户姓名' }]}
            >
              <Input
                placeholder="请输入客户姓名"
                size="large"
                className="form-input"
              />
            </Form.Item>
          </Card>

                  {/* 预约时间 */}
                  <Card className="form-card">
                    <div className="time-section">
                      <Form.Item
                        label={
                          <span className="form-label">
                            <CalendarOutlined className="label-icon" />
                            预约日期
                          </span>
                        }
                        name="appointmentDate"
                        rules={[{ required: true, message: '请选择预约日期' }]}
                      >
                        <DatePicker
                          placeholder="请选择日期"
                          size="large"
                          className="form-date-picker mobile-date-picker"
                          style={{ 
                            width: '100%',
                            maxWidth: '100%',
                            boxSizing: 'border-box'
                          }}
                          format="YYYY-MM-DD"
                          inputReadOnly
                          allowClear={false}
                          disabledDate={disabledDate}
                          popupStyle={{
                            fontSize: '16px'
                          }}
                          onOpenChange={(open) => (open ? lockBodyScroll() : unlockBodyScroll())}
                          onChange={() => {
                            // 日期变化时重置时间和分钟选择
                            form.setFieldsValue({
                              appointmentHour: undefined,
                              appointmentMinute: undefined
                            })
                          }}
                        />
                      </Form.Item>

                      <div className="time-row">
                        <Form.Item
                          label={
                            <span className="form-label">
                              <ClockCircleOutlined className="label-icon" />
                              预约时间
                            </span>
                          }
                          name="appointmentHour"
                          rules={[{ required: true, message: '请选择小时' }]}
                          style={{ flex: 1, marginRight: '8px' }}
                        >
                          <Select
                            placeholder="时"
                            size="large"
                            className="form-select mobile-select"
                            dropdownStyle={{
                              fontSize: '16px',
                              minHeight: '48px'
                            }}
                            onDropdownVisibleChange={(open) => (open ? lockBodyScroll() : unlockBodyScroll())}
                            onChange={() => {
                              // 小时变化时重置分钟选择
                              form.setFieldsValue({
                                appointmentMinute: undefined
                              })
                            }}
                          >
                            {getAvailableHourOptions().map(hour => (
                              <Option key={hour.value} value={hour.value}>
                                {hour.label}
                              </Option>
                            ))}
                          </Select>
                        </Form.Item>

                        <Form.Item
                          name="appointmentMinute"
                          rules={[{ required: true, message: '请选择分钟' }]}
                          style={{ flex: 1, marginLeft: '8px' }}
                        >
                          <Select
                            placeholder="分"
                            size="large"
                            className="form-select mobile-select"
                            dropdownStyle={{
                              fontSize: '16px',
                              minHeight: '48px'
                            }}
                            onDropdownVisibleChange={(open) => (open ? lockBodyScroll() : unlockBodyScroll())}
                          >
                            {getAvailableMinuteOptions().map(minute => (
                              <Option key={minute.value} value={minute.value}>
                                {minute.label}
                              </Option>
                            ))}
                          </Select>
                        </Form.Item>
                      </div>
                    </div>
                  </Card>

          {/* 销售顾问电话 */}
          <Card className="form-card">
            <Form.Item
              label={
                <span className="form-label">
                  <PhoneOutlined className="label-icon" />
                  销售顾问电话
                </span>
              }
              name="consultantPhone"
              rules={[
                { required: true, message: '请输入销售顾问电话' },
                { pattern: /^1[3-9]\d{9}$/, message: '请输入正确的手机号码' }
              ]}
            >
              <Input
                type="tel"
                inputMode="numeric"
                placeholder="请输入销售顾问电话"
                size="large"
                className="form-input"
                maxLength={11}
              />
            </Form.Item>
          </Card>

          {/* 经销商选择 */}
          <Card className="form-card">
            <div className="dealer-section">
              <div className="dealer-label">
                <ShopOutlined className="label-icon" />
                选择经销商
              </div>
              
              {selectedDealer ? (
                <>
                  <div className="dealer-info">
                    <div className="dealer-name">{selectedDealer.name}</div>
                    <div className="dealer-address">{selectedDealer.address}</div>
                    {selectedDealer.sj_code && (
                      <div className="dealer-sj-code">SJ代码: {selectedDealer.sj_code}</div>
                    )}
                  </div>
                  <Button
                    type="default"
                    size="large"
                    onClick={handleDealerSelect}
                    className="dealer-select-btn"
                    block
                  >
                    重新选择经销商
                  </Button>
                </>
              ) : (
                <Button
                  type="primary"
                  size="large"
                  onClick={handleDealerSelect}
                  className="dealer-select-btn"
                  block
                >
                  选择经销商
                </Button>
              )}
            </div>
          </Card>
          </Form>
        )}

        {/* 强制内容缓冲区域 - 防止被底部按钮遮挡 */}
        <div className="content-buffer-zone">
          <div className="buffer-content">
            {/* 这个区域确保内容不被底部按钮遮挡 */}
          </div>
        </div>

        {/* 底部按钮 */}
        <div className="action-buttons">
          <Space size="middle" className="button-group">
            <Button
              size="large"
              onClick={handleCancel}
              className="cancel-btn"
            >
              取消
            </Button>
            <Button
              type="primary"
              size="large"
              onClick={handleCreate}
              className="create-btn"
            >
              创建邀请函
            </Button>
          </Space>
        </div>
      </div>

      {/* 成功弹窗 */}
      <Modal
        title="创建成功"
        open={successModalVisible}
        onOk={handleSuccessModalOk}
        onCancel={handleSuccessModalOk}
        okText={sendingMessage ? "获取中..." : "好的"}
        cancelButtonProps={{ style: { display: 'none' } }}
        centered
        width="90%"
        style={{ maxWidth: '400px' }}
        confirmLoading={sendingMessage}
      >
        <div style={{ textAlign: 'center', padding: '20px 0' }}>
          <div style={{ fontSize: '16px', color: '#333', marginBottom: '16px' }}>
          邀请函创建成功！请在新页面点击右上角"…"，选择"分享给微信好友"，选择对应客户即可发送。
          </div>
        </div>
      </Modal>
    </div>
  )
}

export default TestDriveInvitationPage
